import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminDashboardData } from '../hooks/useAdminDashboardData';
import PlantSummaryCards from '../components/admin-dashboard/PlantSummaryCards';
import PlantPnidTable from '../components/admin-dashboard/PlantPnidTable';

export default function AdminDashboardPage() {
  const { data, loading, error, refresh } = useAdminDashboardData();
  const navigate = useNavigate();

  const stats = useMemo(() => {
    const summary = data?.plant_summary ?? [];
    const rows = data?.pnid_detail ?? [];
    const avgRows = data?.avg_time_per_plant ?? [];

    const total = summary.reduce((sum, plant) => sum + plant.total, 0) || rows.length;
    const completed = summary.reduce((sum, plant) => sum + plant.completed, 0);
    const inProgress = summary.reduce((sum, plant) => sum + plant.in_progress, 0);
    const todo = summary.reduce((sum, plant) => sum + plant.todo, 0);
    const assigned = rows.filter((row) => row.user && row.user.trim()).length;
    const unassigned = Math.max(rows.length - assigned, 0);
    const completionPct = total > 0 ? Math.round((completed / total) * 100) : 0;

    const diagramsWithTime = avgRows.reduce((sum, row) => sum + row.diagrams_with_time, 0);
    const weightedAvg = diagramsWithTime > 0
      ? avgRows.reduce((sum, row) => sum + row.avg_minutes * row.diagrams_with_time, 0) / diagramsWithTime
      : null;

    const lastUpdated = rows.reduce<string | null>((latest, row) => {
      if (!row.updated_at) return latest;
      if (!latest) return row.updated_at;
      return new Date(row.updated_at).getTime() > new Date(latest).getTime()
        ? row.updated_at
        : latest;
    }, null);

    return {
      total,
      completed,
      inProgress,
      todo,
      assigned,
      unassigned,
      completionPct,
      plantCount: summary.length,
      avgMinutes: weightedAvg,
      lastUpdated,
    };
  }, [data]);

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div style={headerInnerStyle}>
          <div style={headerCopyStyle}>
            <p style={eyebrowStyle}>P&ID Admin</p>
            <h1 style={titleStyle}>Operations Dashboard</h1>
            <div style={metaRowStyle}>
              <span>{stats.plantCount || '-'} plants</span>
              <span>{stats.total || '-'} diagrams</span>
              <span>Last update {formatDateTime(stats.lastUpdated)}</span>
            </div>
          </div>
          <div style={headerActionsStyle}>
            <button onClick={() => navigate('/')} style={secondaryBtnStyle}>
              Open Tool
            </button>
            <button onClick={() => navigate('/admin')} style={secondaryBtnStyle}>
              Form Config
            </button>
            <button onClick={refresh} disabled={loading} style={primaryBtnStyle}>
              {loading ? 'Refreshing...' : 'Refresh'}
            </button>
          </div>
        </div>
        <nav style={navStyle} aria-label="Admin sections">
          <button type="button" style={activeNavItemStyle}>
            Operations
          </button>
          <button type="button" onClick={() => navigate('/admin')} style={navItemStyle}>
            Form schema
          </button>
        </nav>
      </header>

      {error && (
        <div style={errorStyle}>
          Failed to load dashboard: {error}
        </div>
      )}

      {loading && !data && (
        <div style={loadingStyle}>Loading dashboard data...</div>
      )}

      {data && (
        <main style={contentStyle}>
          <section style={metricGridStyle} aria-label="Admin dashboard totals">
            <MetricCard
              label="Total diagrams"
              value={stats.total}
              detail={`${stats.plantCount} plants in scope`}
            />
            <MetricCard
              label="Completed"
              value={stats.completed}
              detail={`${stats.completionPct}% of diagram set`}
              tone="success"
            />
            <MetricCard
              label="In progress"
              value={stats.inProgress}
              detail={`${stats.todo} waiting`}
              tone="warning"
            />
            <MetricCard
              label="Assigned"
              value={stats.assigned}
              detail={`${stats.unassigned} unassigned`}
            />
            <MetricCard
              label="Avg review time"
              value={stats.avgMinutes == null ? '-' : `${Math.round(stats.avgMinutes)}m`}
              detail="Weighted by diagrams with timing"
            />
          </section>

          <PlantSummaryCards
            summary={data.plant_summary}
            avgTime={data.avg_time_per_plant}
          />
          <PlantPnidTable
            data={data.pnid_detail}
            onOpenDocument={(docId) => navigate(`/pnid/${docId}`)}
          />
        </main>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  label: string;
  value: number | string;
  detail: string;
  tone?: 'neutral' | 'success' | 'warning';
}) {
  const toneStyle = tone === 'success'
    ? successMetricStyle
    : tone === 'warning'
      ? warningMetricStyle
      : neutralMetricStyle;

  return (
    <div style={{ ...metricCardStyle, ...toneStyle }}>
      <span style={metricLabelStyle}>{label}</span>
      <strong style={metricValueStyle}>{value}</strong>
      <span style={metricDetailStyle}>{detail}</span>
    </div>
  );
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const pageStyle: React.CSSProperties = {
  minHeight: '100%',
  backgroundColor: '#f8fafc',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle: React.CSSProperties = {
  backgroundColor: '#fff',
  borderBottom: '1px solid #e5e7eb',
  position: 'sticky',
  top: 0,
  zIndex: 10,
};

const headerInnerStyle: React.CSSProperties = {
  maxWidth: 1320,
  margin: '0 auto',
  padding: '18px 32px 12px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 24,
  flexWrap: 'wrap',
};

const headerCopyStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const eyebrowStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: '#64748b',
};

const titleStyle: React.CSSProperties = {
  fontSize: 24,
  lineHeight: 1.2,
  fontWeight: 700,
  color: '#0f172a',
  margin: 0,
};

const metaRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
  fontSize: 13,
  color: '#64748b',
};

const headerActionsStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const navStyle: React.CSSProperties = {
  maxWidth: 1320,
  margin: '0 auto',
  padding: '0 32px',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  overflowX: 'auto',
};

const navItemStyle: React.CSSProperties = {
  padding: '10px 12px',
  border: 'none',
  borderBottom: '2px solid transparent',
  background: 'transparent',
  color: '#64748b',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const activeNavItemStyle: React.CSSProperties = {
  ...navItemStyle,
  color: '#0f172a',
  borderBottomColor: '#2563eb',
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 700,
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  backgroundColor: '#fff',
  color: '#334155',
  cursor: 'pointer',
};

const primaryBtnStyle: React.CSSProperties = {
  ...secondaryBtnStyle,
  borderColor: '#1d4ed8',
  backgroundColor: '#2563eb',
  color: '#fff',
};

const errorStyle: React.CSSProperties = {
  maxWidth: 1320,
  margin: '24px auto 0',
  padding: '12px 16px',
  backgroundColor: '#fef2f2',
  color: '#b91c1c',
  border: '1px solid #fecaca',
  borderRadius: 8,
  fontSize: 14,
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 80,
  color: '#9ca3af',
  fontSize: 16,
};

const contentStyle: React.CSSProperties = {
  padding: '24px 32px 40px',
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
  maxWidth: 1320,
  margin: '0 auto',
};

const metricGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};

const metricCardStyle: React.CSSProperties = {
  minHeight: 112,
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  backgroundColor: '#fff',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: 8,
};

const neutralMetricStyle: React.CSSProperties = {
  borderTop: '3px solid #64748b',
};

const successMetricStyle: React.CSSProperties = {
  borderTop: '3px solid #059669',
};

const warningMetricStyle: React.CSSProperties = {
  borderTop: '3px solid #d97706',
};

const metricLabelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const metricValueStyle: React.CSSProperties = {
  fontSize: 30,
  lineHeight: 1,
  fontWeight: 750,
  color: '#0f172a',
};

const metricDetailStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#64748b',
};
